import { useEffect, useState, useCallback } from 'react';
import Cookies from 'universal-cookie';
import usePageType from '@hooks/use-page-type';
import { AppCookies } from '@enums/cookies';
import { usePreferences } from '@context/preferences.context';
import { useRouter } from 'next/router';
import { getRegionIdByRegionName } from '@helpers/localization';
import { isPricingCalculatorPage, isWebview } from '@helpers/routing';
import { useDeviceSize } from '@context/device-size.context';
import { DeviceType } from '@enums/deviceType';
import { getVisitorIdentityFromCookie } from '@helpers/utils/visitor-identity-cookie';
import { VisitorType } from '@enums/visitor-identity';

const cookies = new Cookies();
const ALLOWED_PAGETYPES = ['HomePage', 'CategoryPage', 'ProductPage', 'HeroPDPPage'];

const useSmartBannerV2FlagGate = () => {
  const { pageType } = usePageType();
  const { siteId } = usePreferences();
  const { pathname, query } = useRouter();
  const isPCPage = isPricingCalculatorPage(pathname);
  const { deviceType } = useDeviceSize();
  const isWebView = isWebview(query);
  const isKr = siteId === getRegionIdByRegionName('KR');
  const getVisitorIdentity = getVisitorIdentityFromCookie();

  const isNaverTraffic = query.utm_medium === 'naver_shopping' && query.utm_source === 'naver';
  // Hide smart banner on Pricing Calculator mWeb (isPCpage)
  const hideSmartBanner = deviceType === DeviceType.DESKTOP || (isKr && isNaverTraffic) || isPCPage || isWebView;

  const [hasBigBannerCookie, setHasBigBannerCookie] = useState<boolean | null>(null);

  const isOnAllowedPage = ALLOWED_PAGETYPES.includes(pageType);

  useEffect(() => {
    setHasBigBannerCookie(!!cookies.get(AppCookies.BIG_SMART_BANNER_CLOSED));
  }, []);

  const shouldShowSmartBannerV2 = useCallback(() => {
    if (hasBigBannerCookie === null) {
      return false;
    }

    const isUserLegit = getVisitorIdentity?.visitorType === VisitorType.NormalUser;
    const canShowSmartBanner = isUserLegit && !hideSmartBanner;

    if (!canShowSmartBanner) {
      return false;
    }

    return true;
  }, [hideSmartBanner, hasBigBannerCookie]);

  return {
    shouldShowSmartBannerV2: shouldShowSmartBannerV2(),
    shouldReserveSmartBannerSpace: hasBigBannerCookie === null && !hideSmartBanner && !isOnAllowedPage,
    isOnAllowedPage,
    hasBigBannerCookie,
  };
};

export default useSmartBannerV2FlagGate;
